/*
  The React wrapper over tableState.ts. All the logic is there; this holds it and hands
  back four setters.

  It exists so that the five tables wire filter, sort and pagination the same way:

    const table = useTableState({ orderBy: "setName", order: "asc" });
    const view = useMemo(
      () => selectTableRows(allRows, columns, table.state),
      [allRows, columns, table.state]
    );

    <FilterSearch onChange={table.setFilter} label="Search Sets" />
    <DataTable columns={columns} rows={view.rows} sort={table.state.sort}
               onSortChange={table.setSort} isLoading={isFetching} error={error} ... />
    <DataTablePagination page={view.page} rowsPerPage={table.state.rowsPerPage}
                         count={view.filteredCount}
                         onChangePage={table.setPage}
                         onChangeRowsPerPage={table.setRowsPerPage} />

  Note which page the pagination is given: view.page, the clamped one, not state.page.

  A server-paginated table (SearchCardsRowTable, task 10) does not use this — it has no
  client-side rows to filter or sort. It uses DataTablePagination on its own, with the
  page it already holds.
*/

import { useCallback, useMemo, useReducer } from "react";

import {
  createTableState,
  tableStateReducer,
  TableState,
  TableStateInit,
} from "./tableState";
import { TableSortState } from "./types";

export interface TableController {
  state: TableState;
  /* Also resets the page to 0 — there is no setter that does one without the other. */
  setFilter: (value: string) => void;
  setSort: (sort: TableSortState) => void;
  setPage: (page: number) => void;
  /* Also resets the page to 0. */
  setRowsPerPage: (rowsPerPage: number) => void;
}

export const useTableState = (init: TableStateInit = {}): TableController => {
  /*
    The initialiser runs once. `init` is spread into it rather than watched, so a view
    passing a fresh object literal every render does not reset the table under the
    visitor — the initial sort is initial, not a prop.
  */
  const [state, dispatch] = useReducer(
    tableStateReducer,
    init,
    createTableState
  );

  const setFilter = useCallback(
    (value: string) => dispatch({ type: "filter", value }),
    []
  );
  const setSort = useCallback(
    (sort: TableSortState) => dispatch({ type: "sort", value: sort }),
    []
  );
  const setPage = useCallback(
    (page: number) => dispatch({ type: "page", value: page }),
    []
  );
  const setRowsPerPage = useCallback(
    (rowsPerPage: number) => dispatch({ type: "rowsPerPage", value: rowsPerPage }),
    []
  );

  return useMemo(
    () => ({ state, setFilter, setSort, setPage, setRowsPerPage }),
    [state, setFilter, setSort, setPage, setRowsPerPage]
  );
};
